import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';

// Sterile Mate Logo URL
const STERILE_MATE_LOGO = "https://customer-assets.emergentagent.com/job_medrec-app-5/artifacts/vhygpw4i_Sterile%20Mate%20Logo%20%282%29.jpg";

// Color scheme
const colors = {
  primary: '#003366',
  secondary: '#0056A8',
  accent: '#4A90D9',
  dark: '#1E293B',
  light: '#F8FAFC',
  muted: '#64748B',
  border: '#CBD5E1',
  white: '#FFFFFF',
};

// Styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    backgroundColor: colors.white,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 15,
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: colors.primary,
  },
  logo: {
    width: 120,
    height: 50,
    objectFit: 'contain',
  },
  headerRight: {
    alignItems: 'flex-end',
  },
  headerTitle: {
    fontSize: 10,
    color: colors.muted,
  },
  productTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 5,
  },
  productSubtitle: {
    fontSize: 14,
    color: colors.secondary,
    marginBottom: 15,
  },
  description: {
    fontSize: 10,
    lineHeight: 1.6,
    color: colors.dark,
    marginBottom: 15,
    textAlign: 'justify',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.white,
    backgroundColor: colors.primary,
    padding: 6,
    marginBottom: 8,
    marginTop: 10,
  },
  table: {
    marginBottom: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRowAlt: {
    backgroundColor: '#F1F5F9',
  },
  tableCell: {
    flex: 1,
    padding: 5,
    fontSize: 9,
    color: colors.dark,
  },
  tableCellLabel: {
    flex: 1,
    padding: 5,
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.primary,
    backgroundColor: '#E2E8F0',
  },
  tableCellValue: {
    flex: 1.5,
    padding: 5,
    fontSize: 9,
    color: colors.dark,
  },
  featureRow: {
    flexDirection: 'row',
    marginBottom: 6,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  featureLabel: {
    width: '25%',
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.primary,
  },
  featureValue: {
    width: '40%',
    fontSize: 9,
    color: colors.dark,
  },
  featureModel: {
    width: '35%',
    fontSize: 8,
    color: colors.muted,
  },
  twoColumn: {
    flexDirection: 'row',
    gap: 15,
  },
  column: {
    flex: 1,
  },
  optionItem: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  bullet: {
    width: 12,
    fontSize: 9,
    color: colors.secondary,
  },
  optionText: {
    flex: 1,
    fontSize: 9,
    color: colors.dark,
  },
  note: {
    fontSize: 8,
    color: colors.muted,
    fontStyle: 'italic',
    marginTop: 2,
    marginLeft: 12,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: colors.border,
    paddingTop: 8,
  },
  footerText: {
    fontSize: 7,
    color: colors.muted,
  },
  footerContact: {
    fontSize: 7,
    color: colors.secondary,
  },
  highlightBox: {
    backgroundColor: '#EFF6FF',
    borderWidth: 1,
    borderColor: colors.accent,
    borderRadius: 4,
    padding: 10,
    marginVertical: 10,
  },
  highlightText: {
    fontSize: 9,
    color: colors.dark,
    textAlign: 'center',
  },
  specValue: {
    fontSize: 14,
    fontWeight: 'bold',
    color: colors.primary,
    textAlign: 'center',
  },
});

// Main PDF Document
const ProductDatasheetPDF = () => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header with Logo */}
      <View style={styles.header}>
        <Image src={STERILE_MATE_LOGO} style={styles.logo} />
        <View style={styles.headerRight}>
          <Text style={styles.headerTitle}>Product Datasheet</Text>
          <Text style={{ fontSize: 8, color: colors.muted }}>Healthcare Water Solutions</Text>
        </View>
      </View>

      {/* Product Title */}
      <Text style={styles.productTitle}>TRITTON IXC 850</Text>
      <Text style={styles.productSubtitle}>REVERSE OSMOSIS SYSTEM | Model: ROS-IXC-850T</Text>

      {/* Description */}
      <Text style={styles.description}>
        Compact RO System. TRITTON IXC 850 REVERSE OSMOSIS SYSTEMS are compact units designed to provide productivity, automatic operation and low maintenance. The Tritton IXC 850 reverse osmosis system with its horizontal membrane design combines high quality components and accessories while keeping in mind the importance of cost to benefit ratio. Tritton IXC 850 reverse osmosis systems although light weight and compact, have been designed for ease of maintenance and installation. They offer a high performance, low pressure Hydron™ membrane for 98% rejection and a production range of 850 - 1000 gpd*. Tritton IXC 850 reverse osmosis systems are ideal for diverse applications and can be used for restaurants, coffee shops, ice machines, food & beverages, laboratories, water for steam generators to name a few.
      </Text>

      {/* Key Specifications Highlight */}
      <View style={styles.highlightBox}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-around' }}>
          <View style={{ alignItems: 'center' }}>
            <Text style={styles.specValue}>850-1000 GPD*</Text>
            <Text style={styles.highlightText}>Production</Text>
          </View>
          <View style={{ alignItems: 'center' }}>
            <Text style={styles.specValue}>98%</Text>
            <Text style={styles.highlightText}>Rejection Rate</Text>
          </View>
          <View style={{ alignItems: 'center' }}>
            <Text style={styles.specValue}>75 lbs</Text>
            <Text style={styles.highlightText}>Weight</Text>
          </View>
          <View style={{ alignItems: 'center' }}>
            <Text style={styles.specValue}>26"x23"x20"</Text>
            <Text style={styles.highlightText}>Dimensions</Text>
          </View>
        </View>
      </View>

      {/* Specifications Table */}
      <Text style={styles.sectionTitle}>SPECIFICATIONS</Text>
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <Text style={styles.tableCellLabel}>MODEL</Text>
          <Text style={styles.tableCellValue}>ROS-IXC-850T</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCellLabel}>PRODUCTION (GPD)</Text>
          <Text style={styles.tableCellValue}>850-1000* gpd</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCellLabel}>MEMBRANE</Text>
          <Text style={styles.tableCellValue}>4" x 21" (1)</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCellLabel}>OPERATING PRESSURE</Text>
          <Text style={styles.tableCellValue}>150 - 200 psi</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCellLabel}>CAPACITY ROTARY PUMP</Text>
          <Text style={styles.tableCellValue}>120 gph</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCellLabel}>MOTOR</Text>
          <Text style={styles.tableCellValue}>1/2 HP, 1725 RPM</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCellLabel}>INPUT-OUTPUT CONNECTION</Text>
          <Text style={styles.tableCellValue}>3/8"</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCellLabel}>SYSTEM DIMENSIONS</Text>
          <Text style={styles.tableCellValue}>26" x 23" x 20"</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableCellLabel}>WEIGHT</Text>
          <Text style={styles.tableCellValue}>75 lbs (34.02 kgs)</Text>
        </View>
        <View style={{ ...styles.tableRow, ...styles.tableRowAlt }}>
          <Text style={styles.tableCellLabel}>ELECTRICAL POWER SUPPLY</Text>
          <Text style={styles.tableCellValue}>120V/60 Hz</Text>
        </View>
      </View>

      <Text style={{ fontSize: 8, color: colors.muted, marginBottom: 10 }}>
        *Depending on feed water characteristics through the 4 stages of filtration. The system production rate is based on minimum feed water quality below 1,000 PPM, &lt; 300 PPM hardness, feed water temp 77 °F, and SDI &lt; 5.
      </Text>

      {/* Standard Features */}
      <Text style={styles.sectionTitle}>STANDARD FEATURES</Text>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Membrane Housing</Text>
        <Text style={styles.featureValue}>4" x 21" 304 Stainless Steel</Text>
        <Text style={styles.featureModel}>Hydronix (SPV-4021-12)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Pump</Text>
        <Text style={styles.featureValue}>Brass rotary vane type (coupled to 1725 rpm motor)</Text>
        <Text style={styles.featureModel}></Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Solenoid Valve</Text>
        <Text style={styles.featureValue}>Brass NC, 120V, 3/8" connection</Text>
        <Text style={styles.featureModel}>Hydronix (BSV-38110)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Flow Meter</Text>
        <Text style={styles.featureValue}>Panel mount 0.1-1 gpm range</Text>
        <Text style={styles.featureModel}>Hydronix (PFM-011)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Pressure Gauges</Text>
        <Text style={styles.featureValue}>100 & 300 psi Glycerin filled</Text>
        <Text style={styles.featureModel}>Hydronix (PG25100B & PG25300B)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Waste & Recirc Valve</Text>
        <Text style={styles.featureValue}>316 Stainless Steel Needle Valve</Text>
        <Text style={styles.featureModel}>Hydronix (NV-12SS316)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Filter Housings</Text>
        <Text style={styles.featureValue}>(3) NSF, 10", white, 1/2" connection</Text>
        <Text style={styles.featureModel}>Hydronix (HF2-10WHWH12)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Pre-filters</Text>
        <Text style={styles.featureValue}>(1) Polyspun Sediment 5μ, (1) Carbon Block 5μ</Text>
        <Text style={styles.featureModel}>Hydronix (SDC-25-1005, CB-25-1005)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Post filter</Text>
        <Text style={styles.featureValue}>(1) Carbon Block, 2.5" x 10", 5μ</Text>
        <Text style={styles.featureModel}>Hydronix (CB-25-1005)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>RO Membrane</Text>
        <Text style={styles.featureValue}>(1) Membrane 4" x 21"</Text>
        <Text style={styles.featureModel}>Hydron (HLP-4021)</Text>
      </View>
      
      <View style={styles.featureRow}>
        <Text style={styles.featureLabel}>Frame</Text>
        <Text style={styles.featureValue}>IXC Bend Style Design with Electrostatic Powder Coating</Text>
        <Text style={styles.featureModel}></Text>
      </View>
      
      <View style={{ ...styles.featureRow, borderBottomWidth: 0 }}>
        <Text style={styles.featureLabel}>Controller</Text>
        <Text style={{ ...styles.featureValue, flex: 1.75 }}>Integrated Aquatrol AQT-EC-100 controller: Low pressure switch, Contactor, Level switch, Manual-automatic selector switch, TDS sensor</Text>
      </View>

      {/* Additional Options */}
      <Text style={styles.sectionTitle}>ADDITIONAL OPTIONS</Text>
      <View style={{ marginBottom: 10 }}>
        <View style={styles.optionItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.optionText}>Stainless steel rotary pump</Text>
        </View>
        <View style={styles.optionItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.optionText}>Stainless steel solenoid valve</Text>
        </View>
        <View style={styles.optionItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.optionText}>Post filter DI / Mixed resins</Text>
        </View>
        <Text style={styles.note}>Recommended for Laboratory applications.</Text>
        <View style={styles.optionItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.optionText}>Aquatrol Hydropneumatic 82 gal Tank (ARO-30582V-B114)</Text>
        </View>
        <View style={styles.optionItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.optionText}>Polaris Germicidal Lamp (UVA-6C)</Text>
        </View>
        <View style={styles.optionItem}>
          <Text style={styles.bullet}>•</Text>
          <Text style={styles.optionText}>Faucet for treated water use</Text>
        </View>
        <Text style={styles.note}>Recommended for any application.</Text>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <View>
          <Text style={styles.footerText}>Distributed by Sterile Mate LLC</Text>
          <Text style={styles.footerContact}>www.sterilemate.com | info@sterilemate.com</Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={styles.footerText}>396 W Greens Road, Suite 1139</Text>
          <Text style={styles.footerText}>Houston, TX 77067</Text>
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Text style={styles.footerText}>LIT-ROS-IXC-850T-DS</Text>
          <Text style={styles.footerText}>Rev. 01/2026</Text>
        </View>
      </View>
    </Page>
  </Document>
);

export default ProductDatasheetPDF;
